import { useState, useEffect, useCallback } from 'react'
import Autosuggest from 'react-autosuggest'
import throttle from 'lodash/debounce'
import { groupBy } from 'utils/helpers'
import { makeGetRequest } from 'utils/api'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import { urlMapper } from './helpers'
import cn from 'classnames'
import s from './AutosuggestInput.scss'

const getSuggestions = async (value, cb, count, type) => {
  const query = value.trim()

  if (query === '') {
    return []
  }

  const response = await makeGetRequest(
    `/v1/search/?query=${encodeURIComponent(query)}&count=${count}`
  )
  const results = groupBy(response, 'type')[type]
  cb && cb(results || [])
  return results
}

const getSuggestionValue = suggestion => suggestion.title

const renderSuggestion = (suggestion, { query }) => {
  const matches = match(suggestion.title, query)
  const parts = parse(suggestion.title, matches)

  return (
    <span className={s.resultsItem}>
      {parts.map((part, index) => {
        const className = part.highlight ? s.highlight : null

        return (
          <span className={className} key={index}>
            {part.text}
          </span>
        )
      })}
    </span>
  )
}

const getSectionSuggestions = section => section.results

const shouldRenderSuggestions = value => value.trim().length > 1

const AutosuggestInput = ({
  initialValue,
  type,
  placeholder,
  id,
  onChangeCb,
  length,
  cy,
  className
}) => {
  const [value, setValue] = useState('')
  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    initialValue !== value && setValue(initialValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue])

  const onChange = (event, { newValue }) => {
    setValue(newValue)
    newValue && onChangeCb && onChangeCb(newValue, suggestions)
  }

  const _onSuggestionsFetchRequested = async ({ value }) => {
    getSuggestions(
      value,
      suggestions => {
        setSuggestions(suggestions)
      },
      length,
      type
    )
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSuggestionsFetchRequested = useCallback(throttle(_onSuggestionsFetchRequested, 500), [])

  const onSuggestionsClearRequested = () => {
    setSuggestions([])
  }

  const onSuggestionSelected = (event, { suggestion }) => {
    setValue(value)
    window.open(`${urlMapper[type]}${suggestion.id}`, '_blank')
  }

  const inputProps = {
    placeholder: placeholder,
    value,
    onChange: onChange,
    className: s.autosuggestInput,
    'data-cy': cy
  }

  const containerProps = {
    className: cn(s.autosuggestContainer, className)
  }

  return (
    <Autosuggest
      id={id}
      suggestions={suggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      onSuggestionSelected={onSuggestionSelected}
      inputProps={inputProps}
      containerProps={containerProps}
      shouldRenderSuggestions={shouldRenderSuggestions}
      getSectionSuggestions={getSectionSuggestions}
    />
  )
}

export default AutosuggestInput
